import { useQuery } from '@tanstack/react-query';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { Reservation } from '@/types/table';
import { normalizeTimeForUI, parseDateLocal } from '@/lib/timeFormatUtils';
import { format, subDays, addDays, min, max } from 'date-fns';

interface UseReservationsQueryOptions {
  establishmentId: string | null;
  useAuthenticatedData?: boolean;
  session?: Session | null;
  selectedDate?: Date | null;
  pastDaysOverride?: number;
}

export function reservationsQueryKey(
  establishmentId: string | null,
  dateFrom: string,
  dateTo: string,
  authenticated: boolean
) {
  return ['reservations', establishmentId, dateFrom, dateTo, authenticated] as const;
}

export function computeReservationsDateRange(
  selectedDate: Date | null | undefined,
  pastDaysOverride: number | undefined
) {
  const today = new Date();
  const defaultPastDays = pastDaysOverride ?? 7;
  const defaultPastDate = subDays(today, defaultPastDays);
  const defaultFutureDate = addDays(today, 60);

  const dateFromDate = selectedDate
    ? min([defaultPastDate, selectedDate])
    : defaultPastDate;
  const dateToDate = selectedDate
    ? max([defaultFutureDate, selectedDate])
    : defaultFutureDate;

  return {
    dateFrom: format(dateFromDate, 'yyyy-MM-dd'),
    dateTo: format(dateToDate, 'yyyy-MM-dd'),
  };
}

export function useReservationsQuery(opts: UseReservationsQueryOptions) {
  const {
    establishmentId,
    useAuthenticatedData = false,
    session,
    selectedDate,
    pastDaysOverride,
  } = opts;

  const { dateFrom, dateTo } = computeReservationsDateRange(selectedDate, pastDaysOverride);

  // Si auth requis mais pas de session, ne pas fetch
  const enabled = Boolean(establishmentId) && (!useAuthenticatedData || !!session);

  return useQuery({
    queryKey: reservationsQueryKey(establishmentId, dateFrom, dateTo, useAuthenticatedData),
    queryFn: async (): Promise<Reservation[]> => {
      if (useAuthenticatedData) {
        let query = supabase
          .from('reservations')
          .select(`
            id,
            customer_id,
            number_of_people,
            number_of_children,
            has_stroller,
            date,
            start_time,
            status,
            notes,
            internal_notes,
            custom_answers,
            duration_minutes,
            terrace_preference,
            source,
            cancellation_reason,
            created_at,
            table_id,
            combined_table_id,
            is_locked,
            customers(id, first_name, last_name, email, phone)
          `)
          .gte('date', dateFrom)
          .lte('date', dateTo)
          .order('date', { ascending: true })
          .order('start_time', { ascending: true });

        if (establishmentId) query = query.eq('establishment_id', establishmentId);

        const { data, error } = await query;
        if (error) throw error;

        return (data || []).map((r: any) => ({
          id: r.id,
          supabaseId: r.id,
          tableId: r.table_id || undefined,
          combinedTableId: r.combined_table_id || undefined,
          customerId: r.customer_id || r.customers?.id || undefined,
          customerFirstName: r.customers?.first_name || '',
          customerLastName: r.customers?.last_name || '',
          customerEmail: r.customers?.email || undefined,
          customerPhone: r.customers?.phone || undefined,
          numberOfPeople: r.number_of_people || 0,
          numberOfChildren: r.number_of_children || 0,
          hasStroller: r.has_stroller ?? false,
          terracePreference:
            (r.terrace_preference as 'no_preference' | 'terrace' | 'indoor') ?? 'no_preference',
          durationMinutes: r.duration_minutes ?? undefined,
          date: parseDateLocal(r.date),
          startTime: normalizeTimeForUI(r.start_time),
          status: r.status,
          cancellationReason: r.cancellation_reason || undefined,
          notes: r.notes || undefined,
          internalNotes: r.internal_notes || undefined,
          customAnswers:
            r.custom_answers && typeof r.custom_answers === 'object' &&
            Array.isArray(r.custom_answers.answers) && r.custom_answers.answers.length > 0
              ? {
                  question: String(r.custom_answers.question ?? ''),
                  answers: (r.custom_answers.answers as unknown[]).map(String),
                }
              : undefined,
          source: r.source || undefined,
          isLocked: !!r.is_locked,
          createdAt: new Date(r.created_at),
        })) as Reservation[];
      }

      // Vue publique anonymisée
      let query = supabasePublic
        .from('reservations_availability_view')
        .select('*')
        .gte('date', dateFrom)
        .lte('date', dateTo)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });

      if (establishmentId) query = query.eq('establishment_id', establishmentId);

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((r: any) => ({
        id: r.id,
        tableId: r.table_id || undefined,
        combinedTableId: r.combined_table_id || undefined,
        customerFirstName: '',
        customerLastName: '',
        customerEmail: undefined,
        customerPhone: undefined,
        numberOfPeople: r.number_of_people || 0,
        numberOfChildren: r.number_of_children || 0,
        hasStroller: false,
        durationMinutes: r.duration_minutes ?? undefined,
        date: parseDateLocal(r.date),
        startTime: normalizeTimeForUI(r.start_time),
        status: r.status,
        notes: undefined,
        internalNotes: undefined,
        createdAt: new Date(),
      })) as Reservation[];
    },
    enabled,
    staleTime: 1000 * 30,
    // Public booking: pas de Realtime sur la table (RLS anon fermé) → polling doux
    refetchInterval: useAuthenticatedData ? false : 15_000,
    refetchOnWindowFocus: true,
    retry: 1,
    retryDelay: 2000,
  });
}
